import { difference, union, area, length, kinks, cleanCoords, unkinkPolygon, featureCollection } from '@turf/turf';

/**
 * Топологический движок SPD+ на базе Turf.js
 */

/**
 * Нормализация объекта GeoJSON (Feature или Geometry) в Feature
 */
export function normalizeToFeature(input) {
    if (!input) return null;
    if (input.type === 'Feature') return input;
    if (input.type === 'Polygon' || input.type === 'MultiPolygon') {
        return {
            type: 'Feature',
            properties: {},
            geometry: input
        };
    }
    return null;
}

function safeDifference(poly1, poly2) {
    try {
        const res = difference(poly1, poly2);
        if (res !== undefined) return res;
    } catch (e) {}
    try {
        return difference(featureCollection([poly1, poly2]));
    } catch (e) {}
    return null;
}

function safeUnion(poly1, poly2) {
    try {
        const res = union(poly1, poly2);
        if (res !== undefined) return res;
    } catch (e) {}
    try {
        return union(featureCollection([poly1, poly2]));
    } catch (e) {}
    return null;
}

/**
 * Очистка и исправление геометрии от самопересечений
 */
export function cleanAndValidateGeometry(feat) {
    try {
        const normalized = normalizeToFeature(feat);
        if (!normalized || !normalized.geometry) return null;

        // Очистка дублирующихся координат
        const cleaned = cleanCoords(normalized);

        // Проверка на самопересечения (kinks)
        const selfIntersections = kinks(cleaned);
        if (selfIntersections.features.length > 0) {
            // Исправление самопересекающегося полигона
            const unkinked = unkinkPolygon(cleaned);
            if (unkinked.features.length === 1) {
                return unkinked.features[0];
            } else if (unkinked.features.length > 1) {
                // Объединяем части обратно
                let merged = unkinked.features[0];
                for (let i = 1; i < unkinked.features.length; i++) {
                    const u = safeUnion(merged, unkinked.features[i]);
                    if (u) merged = u;
                }
                return merged;
            }
        }
        return cleaned;
    } catch (err) {
        console.warn('Geometry validation warning:', err);
        return normalizeToFeature(feat);
    }
}

/**
 * Вычитание одного контура из другого (Difference / Cut-out)
 * @param {Object} baseFeature - Основной объект (из которого вычитаем)
 * @param {Object} subtractFeature - Вычитаемый объект (пятно застройки, сервитут, отверстие)
 * @returns {Object|null} Результат вычитания как новый Feature с сохраненными свойствами базы
 */
export function performDifference(baseFeature, subtractFeature) {
    try {
        const base = cleanAndValidateGeometry(baseFeature);
        const subtract = cleanAndValidateGeometry(subtractFeature);

        if (!base || !subtract) {
            throw new Error('Некорректная геометрия исходных объектов для операции вычитания');
        }

        // Выполняем вычитание через Turf
        const diffResult = safeDifference(base, subtract);

        if (!diffResult || !diffResult.geometry) {
            throw new Error('Вычитание привело к полному удалению геометрии целевого объекта');
        }

        // Сохраняем исходные свойства базового объекта и пересчитываем площадь
        const newAreaM2 = Math.round(area(diffResult) * 100) / 100;
        const newAreaSot = Math.round((newAreaM2 / 100) * 100) / 100;

        return {
            type: 'Feature',
            id: baseFeature.id,
            geometry: diffResult.geometry,
            properties: {
                ...baseFeature.properties,
                id: baseFeature.id || baseFeature.properties?.id,
                area: newAreaM2,
                area_sot: newAreaSot,
                modified_by: 'spd_plus_difference',
                modified_at: new Date().toISOString()
            }
        };
    } catch (err) {
        console.error('Error performing difference:', err);
        throw err;
    }
}

/**
 * Объединение нескольких полигонов в единый массив (Union / Merge)
 * @param {Array<Object>} features - Список объектов для объединения (минимум 2)
 * @param {Object} targetProperties - Опциональные свойства целевого объекта
 * @returns {Object} Результат объединения как новый Feature
 */
export function performUnion(features, targetProperties = null) {
    try {
        if (!Array.isArray(features) || features.length < 2) {
            throw new Error('Для объединения необходимо выбрать как минимум 2 объекта');
        }

        const validFeatures = features
            .map(f => cleanAndValidateGeometry(f))
            .filter(Boolean);

        if (validFeatures.length < 2) {
            throw new Error('Недостаточно валидных геометрий для объединения');
        }

        let mergedResult = validFeatures[0];

        for (let i = 1; i < validFeatures.length; i++) {
            const nextFeature = validFeatures[i];
            const unionResult = safeUnion(mergedResult, nextFeature);
            if (!unionResult) {
                throw new Error(`Не удалось объединить объект #${i + 1}`);
            }
            mergedResult = unionResult;
        }

        // Пересчет площади
        const newAreaM2 = Math.round(area(mergedResult) * 100) / 100;
        const newAreaSot = Math.round((newAreaM2 / 100) * 100) / 100;

        // Базовые свойства берем от первого объекта или переданных целевых свойств
        const baseProps = targetProperties || features[0].properties || {};
        const masterId = features[0].id || baseProps.id;

        return {
            type: 'Feature',
            id: masterId,
            geometry: mergedResult.geometry,
            properties: {
                ...baseProps,
                id: masterId,
                area: newAreaM2,
                area_sot: newAreaSot,
                merged_count: features.length,
                modified_by: 'spd_plus_union',
                modified_at: new Date().toISOString()
            }
        };
    } catch (err) {
        console.error('Error performing union:', err);
        throw err;
    }
}

/**
 * Расчет пространственных метрик
 */
export function getFeatureMetrics(feature) {
    try {
        const feat = normalizeToFeature(feature);
        if (!feat || !feat.geometry) return { areaM2: 0, areaSot: 0, perimeterM: 0 };

        const areaM2 = Math.round(area(feat) * 100) / 100;
        const areaSot = Math.round((areaM2 / 100) * 100) / 100;
        const perimeterM = Math.round(length(feat, { units: 'kilometers' }) * 1000 * 100) / 100;

        return { areaM2, areaSot, perimeterM };
    } catch (err) {
        return { areaM2: 0, areaSot: 0, perimeterM: 0 };
    }
}
